import { ChangeDetectionStrategy, Component, computed, effect, forwardRef, signal } from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { disabled, form, FormField, validate } from '@angular/forms/signals';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslatePipe } from '@ngx-translate/core';
import { CountryCode, E164Number, isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js';
import { COUNTRIES } from '../../../../utils/normalize-phone';

/** Numéro E164 valide pour le couple (pays, numéro national), sinon `null`. */
function toValidE164(phoneNumber: string, country: CountryCode): E164Number | null {
  if (!phoneNumber) return null;

  const parsedNumber = parsePhoneNumberFromString(phoneNumber, country);
  if (!parsedNumber || !isValidPhoneNumber(parsedNumber.number, country)) return null;

  return parsedNumber.number;
}

@Component({
  selector: 'phone-input',

  imports: [TranslatePipe, FormField, MatFormFieldModule, MatInputModule, MatSelectModule],
  templateUrl: './phone-input.component.html',
  styleUrl: './phone-input.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true,
    },
  ],
})
export class PhoneInputComponent implements ControlValueAccessor, Validator {
  public countryLib = COUNTRIES;

  private readonly _isDisabled = signal(false);

  private readonly _phoneModel = signal({
    country: 'FR' as CountryCode,
    phoneNumber: '',
  });

  public readonly phoneForm = form(this._phoneModel, schemaPath => {
    disabled(schemaPath, { when: () => this._isDisabled() });

    validate(schemaPath.phoneNumber, ({ value, valueOf }) => {
      if (!value()) return undefined;
      if (!toValidE164(value(), valueOf(schemaPath.country))) {
        return { kind: 'phoneError', message: 'DRIVER.ERROR_PHONE' };
      }
      return undefined;
    });
  });

  /** Valeur exposée au contrôle parent : `null` tant que le numéro n'est pas valide. */
  private readonly _e164Number = computed<E164Number | null>(() => {
    const { country, phoneNumber } = this._phoneModel();
    return toValidE164(phoneNumber, country);
  });

  /** Dernière valeur synchronisée avec le parent, pour ne pas réémettre ce qu'il vient d'écrire. */
  private _syncedNumber: E164Number | null = null;
  /** Erreurs exposées au contrôle parent par `validate()`. */
  private _errors: ValidationErrors | null = null;

  private onChange: (value: E164Number | null) => void = () => {};
  private onTouched: () => void = () => {};
  private _onValidatorChange: () => void = () => {};

  constructor() {
    effect(() => {
      const e164Number = this._e164Number();
      this._errors = this.phoneForm.phoneNumber().invalid() ? { phoneError: true } : null;

      if (e164Number !== this._syncedNumber) {
        this._syncedNumber = e164Number;
        this.onChange(e164Number);
      }

      this._onValidatorChange();
    });

    effect(() => {
      if (this.phoneForm().touched()) {
        this.onTouched();
      }
    });
  }

  writeValue(value: string | null): void {
    this._syncedNumber = value ? toValidE164(value, 'FR') : null;

    const parsedNumber = value && value.length >= 2 ? parsePhoneNumberFromString(value) : null;
    this._phoneModel.set({
      country: parsedNumber?.country ?? 'FR',
      phoneNumber: parsedNumber?.nationalNumber ?? '',
    });
  }

  registerOnChange(fn: (value: E164Number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._isDisabled.set(isDisabled);
  }

  validate(): ValidationErrors | null {
    return this._errors;
  }

  registerOnValidatorChange(fn: () => void): void {
    this._onValidatorChange = fn;
  }
}
